import { useQuery } from "@tanstack/react-query";
import { Dialog } from "radix-ui";
import {
  type CSSProperties,
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { getPage } from "../../lib/api";
import { DESTINATIONS } from "../../lib/destinations";
import { useSession } from "../../lib/session";
import {
  RECORD_LIMIT,
  RECORD_MIN_LENGTH,
  RECORD_SOURCES,
  type RecordSource,
  canSearch,
  pagesFor,
  rankPages,
  shouldSearchRecords,
  stepIndex,
} from "../../lib/spotlight";
import type { ChargePoint, Customer, Identity } from "../../lib/types";

/**
 * The spotlight: Ctrl/⌘+K from anywhere, type, Enter.
 *
 * It finds two kinds of thing. Pages, matched locally against the rail's own
 * list, so jumping to "Audit log" never waits on the network. And records —
 * charge points, customers, identities — from the same `search` the list pages
 * use, a few of each, so "where is CP-0001" is one shortcut rather than a click
 * to Fleet and a filter.
 *
 * Every source is gated on the permission its own page needs, so the palette
 * never offers what the rail would not.
 *
 * A Radix dialog for the parts that are easy to get subtly wrong: focus trapped
 * while open and returned on close, Escape, outside click, scroll lock. The
 * results are an ARIA combobox — focus stays in the input and the active option
 * is announced through `aria-activedescendant` — because that is what lets the
 * arrow keys move the selection while typing continues.
 */

interface SpotlightProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function Spotlight({ open, onOpenChange }: SpotlightProps) {
  // Radix returns focus only to a `Dialog.Trigger`, and this opens from a
  // shortcut as often as from a button — so whatever had focus at opening is
  // remembered here and given it back, the rail button or a half-filled field.
  const returnFocus = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="spotlight-veil fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            returnFocus.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocus.current?.isConnected === true) returnFocus.current.focus();
          }}
          className={[
            "spotlight-panel fixed top-[12vh] left-1/2 z-50 flex max-h-[min(34rem,76vh)] w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 flex-col",
            "overflow-hidden rounded-panel border border-rule bg-paper-raised shadow-float",
          ].join(" ")}
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          {/* Inside the content, which Radix unmounts on close, so every opening
              starts from an empty query rather than the last one. */}
          <SpotlightPanel
            onPick={() => {
              // Navigating: focus belongs to the page being opened, not back on
              // the rail button that opened the palette.
              returnFocus.current = null;
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface Result {
  readonly key: string;
  readonly section: string;
  readonly label: string;
  readonly hint: string;
  readonly to: string;
}

interface RecordResults {
  readonly section: string;
  readonly results: readonly Result[];
  readonly isPending: boolean;
  readonly isError: boolean;
}

const LIST_ID = "spotlight-results";
const DEBOUNCE_MS = 180;

function SpotlightPanel({ onPick }: { readonly onPick: () => void }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const settled = useDebounced(query, DEBOUNCE_MS);
  const listRef = useRef<HTMLDivElement>(null);

  const pages: readonly Result[] = rankPages(pagesFor(DESTINATIONS, can), query).map((page) => ({
    key: `page:${page.to}`,
    section: "Pages",
    label: page.label,
    hint: page.group,
    to: page.to,
  }));

  // Every source's hook runs on every render (hooks cannot be skipped); the one
  // a principal may not search is disabled inside, so it sends no request and
  // contributes no rows.
  const sources: readonly RecordResults[] = [
    useRecords<ChargePoint>(RECORD_SOURCES.chargePoints, settled, (row) => ({
      label: row.id,
      hint: row.depotName,
      to: `/fleet/${encodeURIComponent(row.id)}`,
    })),
    // No customer page of its own: the list's rows open that customer's orders,
    // which is what a driver's question is about, so the palette goes there too.
    useRecords<Customer>(RECORD_SOURCES.customers, settled, (row) => ({
      label: row.displayName ?? row.email,
      hint: row.displayName === null ? "Customer" : row.email,
      to: `/public-orders?customerId=${encodeURIComponent(row.id)}`,
    })),
    useRecords<Identity>(RECORD_SOURCES.identities, settled, (row) => ({
      label: row.label,
      hint: row.idTag,
      to: `/identities?identity=${encodeURIComponent(row.id)}`,
    })),
  ];

  // Record rows belong to the settled query. While the debounce is pending they
  // answer the previous text, and Enter on one would open the wrong record — so
  // until the query settles, only pages (matched live) are offered.
  const isSettled = query === settled;
  const results = [...pages, ...(isSettled ? sources.flatMap((source) => source.results) : [])];
  const active = results.length === 0 ? -1 : Math.min(activeIndex, results.length - 1);
  const activeResult = active === -1 ? undefined : results[active];
  const searching = shouldSearchRecords(query) && (!isSettled || sources.some((s) => s.isPending));

  // Measured before paint, so the highlight is never a frame behind its row.
  // Keyed on the result's key and the result count: the same row can move when
  // results arrive above it, and that move should glide too.
  const [highlight, setHighlight] = useState({ top: 0, height: 0 });
  const activeKey = activeResult?.key;
  const scrolledFor = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const row =
      activeKey === undefined
        ? null
        : listRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(activeKey)}"]`);
    if (row === null || row === undefined) {
      setHighlight((current) => ({ ...current, height: 0 }));
      return;
    }
    setHighlight({ top: row.offsetTop, height: row.offsetHeight });
    // Only when the selection moved: results arriving should not yank the list
    // back from wherever the operator scrolled it.
    if (scrolledFor.current !== activeKey) {
      scrolledFor.current = activeKey;
      row.scrollIntoView({ block: "nearest" });
    }
  }, [activeKey, results.length]);

  const open = (result: Result) => {
    onPick();
    navigate(result.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(stepIndex(active, event.key === "ArrowDown" ? 1 : -1, results.length));
      return;
    }
    // Not while an IME is composing (a Khmer or CJK keyboard): that Enter
    // commits the text, it is not a choice.
    if (event.key === "Enter" && activeResult !== undefined && !event.nativeEvent.isComposing) {
      event.preventDefault();
      open(activeResult);
    }
  };

  const sections = groupBySection(results);
  const failed = sources.filter((source) => source.isError).map((source) => source.section);

  return (
    <>
      <div className="flex items-center gap-3 border-b border-rule px-4">
        <SearchGlyph />
        {/* No `autoFocus`: Radix focuses the first field itself, and React's
            autofocus would land first and be what `returnFocus` records. */}
        <input
          role="combobox"
          aria-label="Search pages and records"
          aria-expanded={results.length > 0}
          aria-controls={LIST_ID}
          aria-autocomplete="list"
          aria-activedescendant={active === -1 ? undefined : optionId(active)}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search pages, charge points, customers, identities…"
          spellCheck={false}
          autoComplete="off"
          className="spotlight-input h-13 min-w-0 flex-1 bg-transparent text-section text-ink placeholder:text-ink-faint"
        />
        <kbd className="rounded-hairline border border-rule px-1.5 py-0.5 text-micro text-ink-faint">
          Esc
        </kbd>
      </div>

      <div className="scroll-slim flex-1 overflow-y-auto p-2">
        <div ref={listRef} id={LIST_ID} role="listbox" aria-label="Results" className="relative">
          <div
            aria-hidden="true"
            className="spotlight-highlight pointer-events-none absolute inset-x-0 top-0 rounded-item bg-paper-sunken"
            style={{
              transform: `translateY(${highlight.top}px)`,
              height: highlight.height,
              opacity: highlight.height === 0 ? 0 : 1,
            }}
          />
          {sections.map(([section, items]) => (
            <div
              key={section}
              role="group"
              aria-labelledby={`spotlight-${slug(section)}`}
              className="pb-2"
            >
              <div
                id={`spotlight-${slug(section)}`}
                className="px-3 pt-2 pb-1 text-micro font-medium uppercase text-ink-faint"
              >
                {section}
              </div>
              {items.map((result) => {
                const index = results.indexOf(result);
                const isActive = index === active;
                return (
                  <div
                    key={result.key}
                    id={optionId(index)}
                    data-key={result.key}
                    role="option"
                    aria-selected={isActive}
                    style={{ "--stage-index": Math.min(index, 10) } as CSSProperties}
                    // Keeps focus in the input, so typing continues after a click
                    // lands on a row that then fails to navigate.
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => open(result)}
                    className={[
                      // Above the highlight, which is behind every row; `stage`
                      // staggers the rows in by position when they arrive.
                      "stage relative flex cursor-pointer items-baseline justify-between gap-4 px-3 py-2",
                      "transition-colors duration-fast ease-swiss",
                      isActive ? "text-ink" : "text-ink-muted",
                    ].join(" ")}
                  >
                    <span
                      className={["truncate text-body", isActive ? "font-semibold" : ""].join(" ")}
                    >
                      {result.label}
                    </span>
                    <span className="shrink-0 truncate text-data text-ink-faint">
                      {result.hint}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Outside the listbox, which may hold only options and groups. */}
        {results.length === 0 && !searching ? (
          <p className="px-3 py-6 text-center text-body text-ink-muted">
            Nothing matches “{query.trim()}”.
          </p>
        ) : null}
      </div>

      {/* One status line rather than a spinner per section: what is still
          loading, what failed, or what it takes to search records at all. */}
      <div
        className="flex items-center justify-between gap-4 border-t border-rule px-4 py-2 text-micro text-ink-faint"
        aria-live="polite"
      >
        <span>
          {failed.length > 0
            ? `Could not search ${failed.join(", ").toLowerCase()}.`
            : searching
              ? "Searching…"
              : shouldSearchRecords(query)
                ? ""
                : `Type ${RECORD_MIN_LENGTH}+ characters to search records`}
          {/* The count, for a screen reader; the list itself shows it. */}
          {!searching ? <span className="sr-only">{`${results.length} results`}</span> : null}
        </span>
        <span className="hidden sm:inline">↑↓ to move · Enter to open</span>
      </div>
    </>
  );
}

/**
 * One kind of record for the current query: the first few matches from that
 * list's own `search`, or nothing when the query is too short or the principal
 * cannot open the list. Hooks, so always called — `enabled` is what skips one.
 */
function useRecords<T extends { readonly id: string }>(
  source: RecordSource,
  query: string,
  describe: (row: T) => Omit<Result, "key" | "section">,
): RecordResults {
  const { can } = useSession();
  const search = query.trim();
  const { section, path } = source;
  const enabled = canSearch(source, can) && shouldSearchRecords(search);

  const records = useQuery({
    queryKey: ["spotlight", path, search],
    queryFn: ({ signal }) => getPage<T>(path, { query: { search, limit: RECORD_LIMIT }, signal }),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    section,
    results: enabled
      ? (records.data?.rows ?? []).map((row) => ({
          key: `${path}:${row.id}`,
          section,
          ...describe(row),
        }))
      : [],
    isPending: enabled && records.isFetching,
    isError: enabled && records.isError,
  };
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}

/** Results grouped under their section, in the order they were flattened. */
function groupBySection(
  results: readonly Result[],
): readonly (readonly [string, readonly Result[]])[] {
  const order: string[] = [];
  const bySection = new Map<string, Result[]>();
  for (const result of results) {
    const existing = bySection.get(result.section);
    if (existing === undefined) {
      order.push(result.section);
      bySection.set(result.section, [result]);
      continue;
    }
    existing.push(result);
  }
  return order.map((section) => [section, bySection.get(section)!] as const);
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** By position: keys hold raw ids, and two ids can slug to the same string. */
function optionId(index: number): string {
  return `spotlight-option-${index}`;
}

function SearchGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-ink-faint"
    >
      <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="m10.5 10.5 3.25 3.25"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}
